export enum CounselRole {
  CoCounsel = 'CoCounsel',
  ReferringCounsel = 'ReferringCounsel',
}

export interface Client {
  id: number;
  firstName: string;
  middleName: string | null;
  lastName: string;
}

export interface FilterTypes {
  firmId?: number | string;
  referenceId?: string;
  clientId?: number | string;
  defendantId?: number | string;
  categoryId?: number | string;
  caseId?: number | string;
  isArchived?: boolean;
  date?: {
    from: Date;
    to: Date;
  };
  from?: string;
  to?: string;
  status?: string;
  role?: string;
  type?: string;
  caseTypeId?: number | string;
  state?: string;
  category?: string;
  statuses?: [string];
  plaintiffId?: number | string;
  accountType?: string;
  transactionType?: string;
  vendorType?: string;
  defaultPaymentMethod?: string;
  vendorStatus?: string;
  verificationStatus?: string;
  vendorId?: number | string;
  mappedDeductionStatus?: string;
  onboardingStatus?: string;
  processingStatus?: string;
  settlementType?: string;
  settlementId?: string;
  view?: string;
}

export type AdminsListData = {
  createdAt: string;
  email: string;
  firstName: string;
  id: number;
  isSuperAdmin: boolean;
  lastName: string;
  phone: string;
  referenceId: string;
  updatedAt: string;
  isEnabled: boolean;
};

export type FirmData = {
  id?: string;
  name: string;
  tin: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  trustAccountNumber: string;
  trustAccountRoutingNumber: string;
  brandColor: string;
  zendeskBrandName: string;
  docusignAccountId: string;
  sendAppInviteTrigger?: string;
  createWalletTrigger?: string;
  issueVirtualCardTrigger?: string;
  issuePhysicalCardTrigger?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  isClientTriggerActive?: boolean;
  logoImageId?: string;
  createdAt?: string;
  isArchived?: string;
};

export type Permissions = {
  [resource: string]: {
    [action: string]: boolean;
  };
};

export type UserRoleApiItem = {
  id: number;
  roleName: string;
};

export type UserRole = {
  id?: number;
  roleName: string;
  isSystemRole?: boolean;
  description?: string | null;
  status?: string;
  permissions?: Permissions;
};

export type User = {
  cognitoId: string | null;
  role: string;
  roles?: (UserRole | string)[];
  createdAt: string;
  updatedAt: string;
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPinSet: boolean;
  status?: string;
  firm: {
    id: string;
    name: string;
    logoImageId: string | null;
    logoImageUrl: string | null;
    showUserOnboardingDetails?: boolean;
    hasSettlementApprovalFlow?: boolean;
    accountingSystem?: AccountingSystem | null;
  };
};

export type SelectOption = {
  id: string;
  label: string;
  defaultPaymentMethod?: string;
  activePaymentMethod?: string[];
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
  accounting?: { isSynced?: boolean; externalVendorId?: string | null };
  counselEntityType?: 'FIRM' | 'VENDOR';
};

export type EwsStatus =
  | 'PENDING'
  | 'NOT_FOUND'
  | 'CLOSED'
  | 'HIGH_RISK'
  | 'INVALID_DEBIT_ACCOUNT'
  | 'OPEN'
  | 'VALIDATED';

export interface VendorAchAccount {
  id: number | string;
  nickname: string;
  accountType: 'Savings' | 'Checking' | string;
  routingNumber?: string;
  maskedAccountNumber?: string;
  accountNumber?: string;
  accountNumberLast4?: string;
  isDefault: boolean;
  isLastUsedAccount?: boolean;
  isUsedAccount?: boolean;
  isNotUsedAccount?: boolean;
  isFailedAccount?: boolean;
  provisionStatus?: ProvisioningStatus | null;
  provisionError?: string | null;
  provisionErrorCode?: string | null;
  ewsStatus?: EwsStatus | null;
}

export interface VendorCheckAccount {
  id: number | string;
  nickname: string;
  payeeName?: string;
  recipientName?: string;
  address: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  isDefault: boolean;
  mailingPhoneNumber: string;
  isLastUsedAccount?: boolean;
  isUsedAccount?: boolean;
  isNotUsedAccount?: boolean;
  isFailedAccount?: boolean;
  provisionStatus?: ProvisioningStatus | null;
  provisionError?: string | null;
  provisionErrorCode?: string | null;
}

export interface PaginationQueryParams {
  size: number;
  page: number;
  count?: boolean;
  match?: string;
  filters?: FilterTypes;
  activeSortField?: ActiveSortField | null;
  includeGlobal?: boolean;
  includeArchived?: boolean;
}

export interface TransactionQueryParams {
  match?: string;
  filters?: FilterTypes;
  activeSortField?: ActiveSortField | null;
  includeGlobal?: boolean;
  includeArchived?: boolean;
}

export type DefendantData = {
  id: number;
  name: string;
  referenceId: string;
  firm: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
};

export type ClientOnboardingStatusItem = {
  onboardingId: number;
  status: string;
  settlementId: number;
  caseReferenceId: string | null;
  createdAt: string;
};

export type ClientSettlementOnboardingInvite = {
  settlementId: number;
  caseId: number;
  caseReferenceId: string | null;
  resendOnboardingInvite: boolean;
};

export type ClientData = {
  customerId?: number;
  resendOnboardingInvite?: boolean;
  settlementOnboardingInvites?: ClientSettlementOnboardingInvite[];
  cognitoId: string | null;
  userId?: string | null;
  status: 'Created' | 'Active' | 'Archived' | 'Deactivated';
  countryState: string;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
  id: number;
  referenceId: string;
  firm: {
    id: number;
    name: string;
  };
  email: string;
  phone: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  address1: string;
  address2: string | null;
  city: string;
  zip: string;
  emailConfirmed: boolean;
  ssnDobActivationPassed: boolean;
  hasSsnDob: boolean;
  additonalInfo: {
    activeCasesCount: number;
    grossSettlements: number;
    netSettlements: number;
  };
  ssnConfirmed: boolean;
  dobConfirmed: boolean;
  onboardingConfiguration?:
  | string
  | {
    authType?: string;
    firmConfig?: string;
    linkDeliveryChannel?: string | null;
    otpChannel?: string | null;
  };
  firmConfigValue?: string;
  onboardingStatus: string;
  onboardingStatuses?: ClientOnboardingStatusItem[];
  onboardingProgress:
    | {
        steps: {
          label: string;
          status: 'Done' | 'Right Tick' | 'In Progress' | 'Pending' | string;
          dateTime: string | null;
          message: string | null;
          failureType: string | null;
        }[];
        paymentMethod: string | null;
      }
    | {
        steps: {
          label: string;
          status: 'Done' | 'Right Tick' | 'In Progress' | 'Pending' | string;
          dateTime: string | null;
          message: string | null;
          failureType: string | null;
        }[];
        paymentMethod: string | null;
        caseId?: number;
        caseReferenceId?: string;
        settlementId?: number;
      }[]
    | null;
  isFboPlaintiff?: boolean;
  payoutProgress:
    | {
        steps: {
          label?: string;
          step?: string;
          claimantStep?: string | null;
          status: 'Done' | 'Right Tick' | 'In Progress' | 'Pending' | string;
          dateTime?: string | null;
          datetime?: string | null;
          message?: string | null;
          errorMessage?: string | null;
          traceNumber?: string | null;
          checkNumber?: string | null;
        }[];
        paymentMethod: string | null;
        caseId?: number;
        caseReferenceId?: string;
        settlementId?: number;
      }[]
    | null;
};

export enum CaseStatus {
  Draft = 'Draft',
  Active = 'Active',
  Closed = 'Closed',
  Abandoned = 'Abandoned',
}

export enum CaseTypeCategory {
  Drugs = 'Drugs',
  MedicalDevices = 'MedicalDevices',
  ToxicExposure = 'ToxicExposure',
  OtherMassTorts = 'OtherMassTorts',
  DisasterLitigation = 'DisasterLitigation',
  PremisesLiability = 'PremisesLiability',
  MovingVehicleInjury = 'MovingVehicleInjury',
  OtherPersonalInjury = 'OtherPersonalInjury',
  Other = 'Other',
  Custom = 'Custom',
}

export interface CaseShort {
  status: CaseStatus;
  filedAt: string | null;
  sol: string | null;
  createdAt: string;
  updatedAt: string;
  id: number;
  referenceId: string;
  name: string;
  client: Client;
  defendants: Array<{
    id: number;
    name: string;
    grossSettlement: number | null;
  }>;
  grossSettlement: number;
  isArchived: boolean;
  isLeadFirm: boolean;
  type: {
    category: CaseTypeCategory;
    id: number;
    name: string;
  };
}

export interface Case extends Omit<CaseShort, 'grossSettlement'> {
  closedAt: string | null;
  abandonedAt: string | null;
  secondaryFirms: Array<{
    role: CounselRole;
    id: number;
    name: string;
    logoImageId: string | null;
    logoImageUrl: string | null;
  }>;
  trustAccount: {
    id: number;
    name: string;
  } | null;
  paymentStarted: boolean;
  canBeAbandoned: boolean;
}

export interface DeductionTimeline {
  status: 'Entered' | 'Paid' | 'Canceled' | 'Unexpected';
  date: string;
}

export type DeductionData = {
  id: number;
  type: string;
  status: string;
  firm: { id: string; name: string };
  case: { id: number; name: string; referenceId: string };
  defendant: { id: number; name: string };
  plaintiff: { id: number; firstName: string; middleName: string; lastName: string };
  createdAt: string;
  paidAt: string;
  amount: number;
  updatedAt: string;
  vendor: { id: number; name: string; defaultPaymentMethod: string };
  mappedDeductionStatus: string;
  fileName?: string;
  docPath?: string;
  description?: string;
  memo?: string;
  disposition?: string;
  recoveredAmount?: number;
};

export type DeductionField = {
  id: number | string;
  vendorName: string;
  description?: string;
  amount: number;
  status: DeductionStatus;
  role?: string;
  paymentMethod?: PaymentMethodStatus;
  paymentAccountId?: number | string | null;
  paymentAccountName?: string | null;
  disposition?: string;
  type: 'Lien' | 'Advance' | 'SERVICE_PROVIDER';
  canRetryVendorPayment?: boolean;
  paymentErrorMessage?: string | null;
  hasDocument?: boolean;
  canStopDeductionTransaction?: boolean;
  accounting?: {
    billLink: string | null;
    billError: string | null;
    billSyncStatus?: AccountingSyncStatus | null;
    billPaymentLink: string | null;
    billPaymentError: string | null;
    billPaymentSyncStatus?: AccountingSyncStatus | null;
    vendorLink?: string | null;
  };
};

export type DraftDeductionField = {
  id: number;
  type: string;
  amount: number;
  vendorParticipationId: number | null;
  vendorName: string | null;
  vendorSuggestions: Array<{
    id: number;
    vendorName: string;
    similarity: number;
    paymentMethod: string;
    provisioning: {
      ach: { isProvisioned: boolean };
      check: { isProvisioned: boolean };
    };
    accounting?: {
      isSynced: boolean;
      externalVendorId: string;
    };
  }>;
  paymentMethod: string;
  vendorPaymentAccountId?: number | null;
  vendorPaymentAccountName?: string | null;
  disposition: string;
  memo: string;
  matchingMode: string | null;
  originalText: string;
  description: string;
  provisioning: {
    ach: { isProvisioned: boolean };
    check: { isProvisioned: boolean };
  };
  accounting?: {
    isSynced: boolean;
    externalVendorId: string;
    link: string | null;
    lastSyncedAt: string | null;
    status: string | null;
  };
};

export enum SettlementStatus {
  Draft = 'Draft',
  Failed = 'Failed',
  Funding = 'Funding',
  InDistribution = 'InDistribution',
  NotStarted = 'NotStarted',
  Paid = 'Paid',
}
export interface SettlementTimeline {
  sequenceNumber: number;
  events: SettlementTimelineSequence[];
}
export interface SettlementTimelineSequence {
  status: SettlementStatus | string;
  date: string;
  statusReadable: string;
  paymentIntention: 'PAY' | 'EXTERNAL' | string;
}

export interface SettlementShort {
  id: number;
  case: {
    id: number;
    referenceId: string;
    name: string;
    type?: {
      id: number;
      name: string;
      firmId: number | null;
      category: string;
    };
  };

  firm: {
    id: number;
    name: string;
  };

  client: Client;

  defendant: {
    id: number;
    name: string;
  };

  settledAmount: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
  settlementType?: 'STANDARD' | 'STANDARD_PIP' | 'LIEN_REPAYMENT' | string;
  createdAt: string;
  updatedAt: string;

  sequences: Array<{
    sequenceNumber: number;
    status: SettlementStatus | string;

    groups?: Array<{
      payeeType: string;
      status: string;
      priority: number;
      parties: Array<{
        id: string;
        name: string;
        payeeType: string;
        status: string;
        priority: number;
        stage: string;
        hasDistributionLeg: boolean;
        amount: string;
        paymentMethod: string;
        isExternal: boolean;
      }>;
    }>;

    attorneyFeesPaidAmount: number | null;
    deductionsPaidAmount: number | null;
    netSettlementPaidAmount: number | null;
    paidAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;

  status: SettlementStatus | string;
  onboardingStatus: string;
  customerId?: number;
  resendOnboardingInvite?: boolean;

  canInitiatePayment?: boolean;
  payGateStatus?: string;
  blockingReason?: string;
  blockingReasonCode?: string;
  activeCycleStatus?: string;
  activeCycleNo?: number;
  canOverride?: boolean;

  approvalTimeline?: ApprovalTimelineEvent[];

  paidAt?: string | null;

  approvalRequired?: boolean;

  approvals?: Array<{
    time: string;
    approvalComment: string;
    approver: string;
    approverIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;

  requestChanges?: Array<{
    time: string;
    changeComment: string;
    requester: string;
    requesterIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;

  pendingApprovals?: Array<{
    roleCode: string;
    isRequired: boolean;
  }>;

  processingStatus?: string;
}

export interface Settlement
  extends Omit<
    SettlementShort,
    'attorneyFeesPaidAmount' | 'deductionsPaidAmount' | 'netSettlementPaidAmount'
  > {
  deductions: Array<{
    type: 'Advance' | 'Lien';
    createdAt: string;
    id: number;
    amount: number;
    isAssigned: boolean;
    vendorName: string | null;
    description: string | null;
    memo: string | null;
    paymentMethod: PaymentMethodStatus | null;
    disposition: DispositionStatus | null;
    validationError: string | null;
    hasDocument: boolean;
    preSettlementAmount?: number;
    accounting?: {
      isSynced: boolean;
      status: string | null;
      externalVendorId: string | null;
      lastSyncedAt: string | null;
      link: string | null;
    };
    included: boolean;
  }> | null;
  secondaryFirms: Array<{
    role: 'CoCounsel' | 'ReferringCounsel';
    id: number;
    name: string;
    accounting?: {
      isSynced: boolean;
      status: string | null;
      externalVendorId: string | null;
      lastSyncedAt: string | null;
      link: string | null;
    };
  }>;
  attorneysFees: {
    total: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
      disposition: string;
      externalPaymentDate: string | null;
      externalPaymentMemo: string | null;
      externalPaymentReference: string | null;
      role: string;
      paymentMethod: string;
      accounting?: {
        isSynced: boolean;
        status: string | null;
        externalVendorId: string | null;
        lastSyncedAt: string | null;
        link: string | null;
      };
    };
    secondaryFirms: Array<{
      id: number;
      name: string;
      role: 'CoCounsel' | 'ReferringCounsel';
      fee: number;
      disposition: string;
      externalPaymentDate: string | null;
      externalPaymentMemo: string | null;
      externalPaymentReference: string | null;
      paymentMethod: string;
      accounting?: {
        isSynced: boolean;
        status: string | null;
        externalVendorId: string | null;
        lastSyncedAt: string | null;
        link: string | null;
      };
    }>;
  };
  netSettlement: {
    clientId: number;
    amount: number;
    name: string;
    paymentMethod: PaymentMethodStatus | null;
    disposition: DispositionStatus | null;
    id: number;
    accounting?: {
      isSynced: boolean;
      status: string | null;
      externalVendorId: string | null;
      lastSyncedAt: string | null;
      link: string | null;
    };
  };
  config?: {
    netSettlementConfigs: Array<{
      sequenceNumber: number;
      paymentIntention: 'PAY' | 'EXTERNAL';
      externalPaymentMethod: string;
      externalPaymentReference: string;
      externalPaymentMemo: string;
      externalPaymentDate: string;
    }> | null;
  };
  withheld: boolean;
  hasActiveOnboarding: boolean;
  isAccountingIntegrated: boolean;
  status: SettlementStatus;
  sequenceNumber: number;
  isToggleOn: boolean;
}

export interface SettlementDistribution {
  sequence: number;
  attorneysFees: {
    total: number;
    totalPaid: number;
    totalWithheld: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
      disposition: string;
      totalPaid: number;
      totalWithheld: number;
    } | null;
    secondaryFirms: Array<{
      role: 'CoCounsel' | 'ReferringCounsel';
      id: number;
      name: string;
      fees: {
        fixed?: number;
        variable?: number;
        total: number;
      };
      disposition: string;
      totalPaid: number;
      totalWithheld: number;
    }>;
    secondaryFirmsTotalPaid: number;
    secondaryFirmsTotalWithheld: number;
    secondaryFirmsCoCounselTotalPaid: number;
    secondaryFirmsCoCounselTotalWithheld: number;
    secondaryFirmsReferingCounselTotalPaid: number;
    secondaryFirmsReferingCounselTotalWithheld: number;
  };
  deductions: {
    advances: number;
    liens: number;
    total: number;
    liensWithheld: number;
    liensNegotiationSavings: number;
    advancesWithheld: number;
    advancesNegotiationSavings: number;
    serviceProviders: number;
    serviceProvidersWithheld: number;
    serviceProvidersNegotiationSavings: number;
    totalPaid: number;
    totalWithheld: number;
    totalNegotiationSavings: number;
    totalLien: number;
    totalAdvance: number;
    totalServiceProvider: number;
  };
  netSettlement: {
    clientId: number;
    amount: number;
    nonNegotiatedAmountToClient?: number;
  };
  currentDisbursement: number;
  previousDisbursement: number;
  balance: number;
}

export interface LienRecoverySummary {
  enteredAmount: number;
  totalRecovered: number;
  alreadyReimbursed: number;
  availableToReimburse: number;
  difference: number;
  hasMismatch: boolean;
}

export interface SettlementPaymentPreview {
  settlementType?: 'STANDARD' | 'STANDARD_PIP' | 'LIEN_REPAYMENT' | string;
  settlementAmount?: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
  lienRecoverySummary?: LienRecoverySummary | null;
  total: number;
  distributions: SettlementDistribution[];
  netSettlementConfig: {
    paymentIntention: 'PAY' | 'EXTERNAL';
    externalPaymentMethod: string;
    externalPaymentReference: string;
    externalPaymentMemo: string;
    externalPaymentDate: string;
  } | null;
  withhold: boolean;
  holdback: number;
  currentDisbursement: number;
  previousDisbursement: number;
  ioltaBalance: number;
  balanceRemaining: number;
  docpath?: string;
  validationError?: string | null;
  warningMessages?: string[];
  canInitiatePayment: boolean;
  payGateStatus: string;
  blockingReason: string;
  blockingReasonCode: string;
  activeCycleStatus: string;
  activeCycleNo: number;
  canOverride: boolean;
  approvalTimeline: ApprovalTimelineEvent[];
  paidAt: string | null;
  updatedAt: string;
  approvalRequired: boolean;
  approvals: Array<{
    time: string;
    approvalComment: string;
    approver: string;
    approverIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;
  requestChanges: Array<{
    time: string;
    changeComment: string;
    requester: string;
    requesterIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;
  pendingApprovals: Array<{
    roleCode: string;
    isRequired: boolean;
  }>;
}

export interface SettlementAdditionalInfo {
  totalAttorneyFeesPercentage: number;
  totalDeductionsPercentage: number;
  netSettlementPercentage: number;
  unpaidAttorneyFees: number;
  unpaidDeductions: number;
}

export enum FundingStatus {
  NotStarted = 'NotStarted',
  Initializing = 'Initializing',
  Processing = 'Processing',
  Error = 'Error',
  CriticalError = 'CriticalError',
  Completed = 'Completed',
}

export enum NetSettlementStatus {
  Failed = 'Failed',
  Funding = 'Funding',
  InDistribution = 'InDistribution',
  NotStarted = 'NotStarted',
  Paid = 'Paid',
}

export enum AttorneysFeeStatus {
  Failed = 'Failed',
  Funding = 'Funding',
  InDistribution = 'InDistribution',
  NotStarted = 'NotStarted',
  Paid = 'Paid',
}

export enum DeductionStatus {
  Failed = 'Failed',
  Funding = 'Funding',
  InDistribution = 'InDistribution',
  NotStarted = 'NotStarted',
  Paid = 'Paid',
  Withhold = 'Withhold',
}

export enum PaymentMethodStatus {
  ACH = 'ACH',
  CHECK = 'CHECK',
  V = 'V',
}

export enum DispositionStatus {
  PAY = 'PAY',
  EXTERNAL = 'EXTERNAL',
  WITHHOLD = 'WITHHOLD',
}

export enum ApprovalStatus {
  WAITING_FOR_SUBMISSION = 'WAITING_FOR_SUBMISSION',
  PENDING_REVIEW = 'PENDING_REVIEW',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  REQUESTED_CHANGES = 'REQUESTED_CHANGES',
  OVERRIDDEN = 'OVERRIDDEN',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export enum SettlementDistributionStatus {
  Paid = 'Paid',
  Funding = 'Funding',
  NotStarted = 'NotStarted',
  Failed = 'Failed',
  InDistribution = 'InDistribution',
}

export enum AccountingSyncStatus {
  NOT_SYNCED = 'NOT_SYNCED',
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SYNCED = 'SYNCED',
  FAILED = 'FAILED',
  RETRY_SCHEDULED = 'RETRY_SCHEDULED',
  MANUALLY_RECORDED = 'MANUALLY_RECORDED',
}

// export interface SettlementPaymentDetails {
//   settledAmount: number;

//   distributions: Array<{
//     sequenceNumber: number;
//     status: string; // e.g., "Draft"

//     attorneysFees: {
//       totalAmount: number;

//       leadFirmFailedCount: number;
//       leadFirmPaidCount: number;
//       leadFirmProcessingCount: number;
//       leadFirmUnpaidCount: number;

//       // CoCounsel totals
//       secondaryFirmsCoCounselTotalPaid: number;
//       secondaryFirmsCoCounselTotalFailed: number;
//       secondaryFirmsCoCounselTotalProcessing: number;
//       secondaryFirmsCoCounselTotalUnpaid: number;

//       // ReferringCounsel totals
//       secondaryFirmsReferingCounselTotalPaid: number;
//       secondaryFirmsReferingCounselTotalFailed: number;
//       secondaryFirmsReferingCounselTotalProcessing: number;
//       secondaryFirmsReferingCounselTotalUnpaid: number;

//       // CoCounsel counts
//       secondaryFirmsCoCounselPaidCount: number;
//       secondaryFirmsCoCounselFailedCount: number;
//       secondaryFirmsCoCounselProcessingCount: number;
//       secondaryFirmsCoCounselUnpaidCount: number;

//       // ReferringCounsel counts
//       secondaryFirmsReferingCounselPaidCount: number;
//       secondaryFirmsReferingCounselFailedCount: number;
//       secondaryFirmsReferingCounselProcessingCount: number;
//       secondaryFirmsReferingCounselUnpaidCount: number;

//       secondaryFirms: Array<{
//         id: number;
//         name: string;
//         role: 'CoCounsel' | 'ReferringCounsel';
//         amount: number;
//         status: string;
//         paymentMethod: string;
//         disposition: 'Pay' | 'PAY';
//         accounting?: {
//           billLink: string | null;
//           billError: string | null;
//           billPaymentLink: string | null;
//           billPaymentError: string | null;
//           vendorLink?: string | null;
//         }
//       }>;

//       leadFirm: {
//         id: number;
//         name: string;
//         amount: number;
//         status: string;
//         role: 'LeadCounsel';
//         paymentMethod: string;
//         disposition: 'Pay' | 'PAY';
//         accounting?: {
//           billLink: string | null;
//           billError: string | null;
//           billPaymentLink: string | null;
//           billPaymentError: string | null;
//           vendorLink?: string | null;
//         }
//       } | null;
//     };

//     deductionsTotalAmount: number;

//     deductions: Array<{
//       id: number;
//       amount: number;
//       type: 'Advance' | 'Lien';
//       status: string;
//       disposition: 'Pay' | 'PAY';
//       vendorName: string | null;
//       paymentMethod: string;
//       paymentErrorMessage: string | null;
//       description?: string | null;
//       canRetryVendorPayment: boolean;
//       preSettlementAmount?: number;
//       hasDocument: boolean;
//       accounting?: {
//         billLink: string | null;
//         billError: string | null;
//         billPaymentLink: string | null;
//         billPaymentError: string | null;
//         vendorLink?: string | null;
//       }
//     }>;

//     netSettlement: {
//       id: number;
//       name: string;
//       amount: number;
//       status: NetSettlementStatus;
//       disposition: DispositionStatus;
//       paymentMethod: PaymentMethodStatus;
//       accounting?: {
//         billLink: string | null;
//         billError: string | null;
//         billPaymentLink: string | null;
//         billPaymentError: string | null;
//         vendorLink?: string | null;
//       }
//     };

//     cancelAvailable: boolean;
//     errorMessage: string | null;
//     retryAvailable: boolean;
//     canRelease: boolean;

//     totalLienPaid: number;
//     totalLienUnpaid: number;
//     totalLienFailed: number;
//     totalLienProcessing: number;

//     totalServiceProviderPaid: number;
//     totalServiceProviderUnpaid: number;
//     totalServiceProviderFailed: number;
//     totalServiceProviderProcessing: number;

//     totalExpensePaid: number;
//     totalExpenseUnpaid: number;
//     totalExpenseFailed: number;
//     totalExpenseProcessing: number;

//     lienPaidCount: number;
//     lienUnpaidCount: number;
//     lienFailedCount: number;
//     lienProcessingCount: number;

//     serviceProviderPaidCount: number;
//     serviceProviderUnpaidCount: number;
//     serviceProviderFailedCount: number;
//     serviceProviderProcessingCount: number;

//     expensePaidCount: number;
//     expenseUnpaidCount: number;
//     expenseFailedCount: number;
//     expenseProcessingCount: number;

//     totalPaidCount: number;
//     totalUnpaidCount: number;
//     totalFailedCount: number;
//     totalProcessingCount: number;

//     negotiationSavings: number;
//     nonNegotiationAmountToClient: number;
//     liensNegotiationSavings: number;
//     advancesNegotiationSavings: number;
//     serviceProvidersNegotiationSavings: number;
//     approvalDetails?: ApprovalDetails;
//   }>;

//   netSettlementConfig: {
//     paymentIntention: 'EXTERNAL' | 'INTERNAL' | string;
//     externalPaymentMethod: string | null;
//     externalPaymentReference: string | null;
//     externalPaymentMemo: string | null;
//     externalPaymentDate: string | null;
//   } | null;

//   docpath?: string;
//   onboardingPendingMessage?: string | null;
//   hasOnboardingPending?: boolean;
//   resendOnboardingInvite?: boolean;
//   customerId?: number;

// }

export interface SettlementPaymentDetails {
  settledAmount: number;
  status: string;

  distributions: Array<{
    sequenceNumber: number;
    status: string;

    attorneysFees: {
      totalAmount: number;

      // Lead firm counts
      leadFirmFailedCount: number;
      leadFirmNotStartedCount: number;
      leadFirmFundingCount: number;
      leadFirmInDistributionCount: number;
      leadFirmPaidCount: number;

      // Co-counsel totals
      secondaryFirmsCoCounselTotalFailed: number;
      secondaryFirmsCoCounselTotalNotStarted: number;
      secondaryFirmsCoCounselTotalFunding: number;
      secondaryFirmsCoCounselTotalInDistribution: number;
      secondaryFirmsCoCounselTotalPaid: number;

      // Referring counsel totals
      secondaryFirmsReferingCounselTotalFailed: number;
      secondaryFirmsReferingCounselTotalNotStarted: number;
      secondaryFirmsReferingCounselTotalFunding: number;
      secondaryFirmsReferingCounselTotalInDistribution: number;
      secondaryFirmsReferingCounselTotalPaid: number;

      // Co-counsel counts
      secondaryFirmsCoCounselFailedCount: number;
      secondaryFirmsCoCounselNotStartedCount: number;
      secondaryFirmsCoCounselFundingCount: number;
      secondaryFirmsCoCounselInDistributionCount: number;
      secondaryFirmsCoCounselPaidCount: number;

      // Referring counsel counts
      secondaryFirmsReferingCounselFailedCount: number;
      secondaryFirmsReferingCounselNotStartedCount: number;
      secondaryFirmsReferingCounselFundingCount: number;
      secondaryFirmsReferingCounselInDistributionCount: number;
      secondaryFirmsReferingCounselPaidCount: number;

      secondaryFirms: Array<{
        id: number;
        name: string;
        role: 'CoCounsel' | 'ReferringCounsel';
        amount: number;
        status: string;
        paymentMethod: string;
        disposition: 'Pay' | 'PAY';
        counselEntityType?: 'FIRM' | 'VENDOR';
        attyFeeId?: number | null;
        vendorParticipationId?: number | null;
        paymentAccountId?: number | null;
        paymentAccountName?: string | null;
        memo?: string | null;
        canRetryVendorPayment?: boolean;
        canStopDeductionTransaction?: boolean;
        paymentErrorMessage?: string | null;
        accounting?: AccountingDetails;
      }>;

      leadFirm: {
        id: number;
        name: string;
        amount: number;
        status: string;
        role: 'LeadCounsel';
        paymentMethod: string;
        disposition: 'Pay' | 'PAY';
        accounting?: AccountingDetails;
      } | null;
    };

    deductions: Array<{
      id: number;
      status: string;
      amount: number;
      type: 'Advance' | 'Lien' | 'SERVICE_PROVIDER';
      disposition: 'Pay' | 'PAY';
      vendorName: string | null;
      paymentMethod: string;
      paymentAccountId?: number | null;
      paymentAccountName?: string | null;
      vendorPaymentAccountId?: number | null;
      vendorPaymentAccountName?: string | null;
      paymentErrorMessage: string | null;
      description: string | null;
      canRetryVendorPayment: boolean;
      canStopDeductionTransaction?: boolean;
      preSettlementAmount: number;
      hasDocument: boolean;
      accounting?: AccountingDetails;
    }>;

    netSettlement: {
      id: number;
      netSettlementId?: number;
      name: string;
      amount: number;
      status: NetSettlementStatus;
      disposition: DispositionStatus;
      paymentMethod: PaymentMethodStatus;
      canRetryPlaintiffPayment: boolean;
      isEligibleForStopTransaction?: boolean;
      isAligibleForStopTransaction?: boolean;
      plaintiffPaymentError?: string | null;
      accounting?: AccountingDetails;
    };

    deductionsTotalAmount: number;

    // Lien totals
    totalLienFailed: number;
    totalLienNotStarted: number;
    totalLienFunding: number;
    totalLienInDistribution: number;
    totalLienPaid: number;

    // Service provider totals
    totalServiceProviderFailed: number;
    totalServiceProviderNotStarted: number;
    totalServiceProviderFunding: number;
    totalServiceProviderInDistribution: number;
    totalServiceProviderPaid: number;

    // Expense totals
    totalExpenseFailed: number;
    totalExpenseNotStarted: number;
    totalExpenseFunding: number;
    totalExpenseInDistribution: number;
    totalExpensePaid: number;

    // Lien counts
    lienFailedCount: number;
    lienNotStartedCount: number;
    lienFundingCount: number;
    lienInDistributionCount: number;
    lienPaidCount: number;

    // Service provider counts
    serviceProviderFailedCount: number;
    serviceProviderNotStartedCount: number;
    serviceProviderFundingCount: number;
    serviceProviderInDistributionCount: number;
    serviceProviderPaidCount: number;

    // Expense counts
    expenseFailedCount: number;
    expenseNotStartedCount: number;
    expenseFundingCount: number;
    expenseInDistributionCount: number;
    expensePaidCount: number;

    // Net settlement counts
    netSettlementFailedCount: number;
    netSettlementNotStartedCount: number;
    netSettlementFundingCount: number;
    netSettlementInDistributionCount: number;
    netSettlementPaidCount: number;

    // Overall counts
    totalFailedCount: number;
    totalNotStartedCount: number;
    totalFundingCount: number;
    totalInDistributionCount: number;
    totalPaidCount: number;

    negotiationSavings: number;
    liensNegotiationSavings: number;
    advancesNegotiationSavings: number;
    serviceProvidersNegotiationSavings: number;
    nonNegotiationAmountToClient: number;

    cancelAvailable: boolean;
    errorMessage: string | null;
    retryAvailable: boolean;
    canRelease: boolean;

    netSettlementConfig: {
      sequenceNumber: number;
      paymentIntention: 'EXTERNAL' | 'INTERNAL' | string;
      externalPaymentMethod: string | null;
      externalPaymentReference: string | null;
      externalPaymentMemo: string | null;
      externalPaymentDate: string | null;
    } | null;

    approvalDetails?: ApprovalDetails;
  }>;

  docpath: string | null;
  onboardingPendingMessage: string | null;
  hasOnboardingPending: boolean;
  isAccountingIntegrated: boolean;
  customerId: number;
  resendOnboardingInvite: boolean;
  isAccountingRetryAvailable?: boolean;
}

interface AccountingDetails {
  billLink: string | null;
  billError: string | null;
  billSyncStatus: AccountingSyncStatus | null;
  billPaymentLink: string | null;
  billPaymentError: string | null;
  billPaymentSyncStatus: AccountingSyncStatus | null;
  vendorLink?: string | null;
}

export interface ApprovalDetails {
  payGateStatus: string;
  activeCycleStatus: string;
  activeCycleNo: number;

  approvals: Array<{
    time: string;
    approvalComment: string;
    approver: string;
    approverIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;

  requestChanges: Array<{
    time: string;
    changeComment: string;
    requester: string;
    requesterIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;

  pendingApprovals: Array<{
    roleCode: string;
    isRequired: boolean;
  }>;

  approvalTimeline: ApprovalTimelineEvent[];
}

export enum DocumentRequestStatus {
  Draft = 'Draft',
  Unopened = 'Unopened',
  Opened = 'Opened',
  Canceled = 'Canceled',
  Rejected = 'Rejected',
  Completed = 'Completed',
}

export interface DocumentRequestTimeline {
  status: DocumentRequestStatus;
  date: string;
}

export type DocumentData = {
  id: number;
  status: string;
  updatedAt: string;
  case: { id: number; name: string; referenceId: string };
  completedAt: string;
  createdAt: string;
  fileId: string;
  firm: { id: string; name: string };
  name: string;
  client: Client;
  sentAt: string;
  fileName: string | null;
};

export enum SignatureRequestStatus {
  Created = 'Created',
  Opened = 'Opened',
  Canceled = 'Canceled',
  CancelInProgress = 'CancelInProgress',
  Rejected = 'Rejected',
  Completed = 'Completed',
}

export interface SignatureRequestTimeline {
  status: SignatureRequestStatus;
  date: string;
}

export type SignatureData = {
  id: number;
  status: string;
  updatedAt: string;
  case: { id: number; name: string; referenceId: string };
  completedAt: string;
  createdAt: string;
  documentId: string;
  firm: { id: string; name: string };
  name: string;
  client: Client;
};

export interface TimelineEvent {
  status: string;
  date: string;
}

export interface SortableFieldsObject {
  [key: string]: {
    order: string;
    fieldName: string;
  };
}

export interface ActiveSortField {
  order: string;
  fieldName: string;
}

export type EntityFlexibleRecord = { entityData: { [key: string]: unknown } };

export interface ClientTransactionsData {
  accountId: string;
  amount: string;
  lastUpdatedOn: string;
  memo: string;
  method: string;
  runningBalance: string;
  scheduleClass: string;
  transactionId: string;
  type: string;
}

export type TrustAccountShortData = {
  id: number;
  firmId: number;
  name: string;
  accountNumber: string;
  routingNumber: string;
};

export type CaseTypeData = {
  category: CaseTypeCategory;
  id: number;
  name: string;
  firmId: number | null;
  trustAccounts: TrustAccountShortData[];
};

export type VendorTypeData = {
  category: CaseTypeCategory;
  id: number;
  name: string;
  firmId: number | null;
  trustAccounts: TrustAccountShortData[];
};

export type ErrorData = {
  error: string;
  message: string;
  statusCode: number;
};

export type QuestionnaireBooleanAnswer = {
  itemId: string;
  format: QuestionnaireItemFormat.Boolean;
  value?: boolean | null;
};

export type QuestionnaireDateAnswer = {
  itemId: string;
  format: QuestionnaireItemFormat.Date;
  value?: string | null;
};

export type QuestionnaireDateRangeAnswer = {
  itemId: string;
  format: QuestionnaireItemFormat.DateRange;
  value?: { from?: string | null; to?: string | null } | null;
};

export type QuestionnaireTextAnswer = {
  itemId: string;
  format: QuestionnaireItemFormat.Text;
  value?: string | null;
};

export type QuestionnaireAnswer =
  | QuestionnaireBooleanAnswer
  | QuestionnaireDateAnswer
  | QuestionnaireDateRangeAnswer
  | QuestionnaireTextAnswer;

export enum DataRequestStatus {
  Draft = 'Draft',
  Unopened = 'Unopened',
  Opened = 'Opened',
  Canceled = 'Canceled',
  Completed = 'Completed',
  Archived = 'Archived',
}

export interface DataRequestTimeline {
  status: DataRequestStatus;
  date: string;
}

export type DataRequestShortData = {
  status: string;
  sentAt: string | null;
  completedAt: string | null;
  id: number;
  name: string;
  case: {
    id: number;
    name: string;
    referenceId: string;
    type: {
      id: number;
      name: string;
    };
  };
  firm: {
    id: number;
    name: string;
  };
  client: Client;
  message: string | null;
  formItemsCount: number;
  formItemAnswersCount: number;
};

export type DataRequestData = DataRequestShortData & {
  form: QuestionnaireForm | null;
  answers: QuestionnaireAnswer[];
  createdAt: string;
  updatedAt: string;
  notes: string | null;
};

export enum QuestionnaireItemFormat {
  Boolean = 'boolean',
  Date = 'date',
  DateRange = 'date_range',
  Text = 'text',
}

type QuestionnaireItemCommonProps = {
  id: string;
  code: string;
  systemName: string;
  questionText: string;
  immutable?: boolean | null;
  dependsOnItem?: {
    booleanItemId: string;
    expectedValue: boolean;
  } | null;
};

export type QuestionnaireBooleanItem = QuestionnaireItemCommonProps & {
  format: QuestionnaireItemFormat.Boolean;
};

export type QuestionnaireDateItem = QuestionnaireItemCommonProps & {
  format: QuestionnaireItemFormat.Date;
};

export type QuestionnaireDateRangeItem = QuestionnaireItemCommonProps & {
  format: QuestionnaireItemFormat.DateRange;
  allowPartial?: boolean | null;
};

export type QuestionnaireTextItem = QuestionnaireItemCommonProps & {
  format: QuestionnaireItemFormat.Text;
  placeholder?: string | null;
};

export type QuestionnaireItem =
  | QuestionnaireBooleanItem
  | QuestionnaireDateItem
  | QuestionnaireDateRangeItem
  | QuestionnaireTextItem;

export type QuestionnaireSection = {
  title: string;
  items: QuestionnaireItem[];
  immutable?: boolean | null;
};

export type QuestionnaireForm = {
  sections: QuestionnaireSection[];
};

export type QuestionnaireShortData = {
  firm: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  id: string;
  name: string;
  caseType: {
    id: number;
    name: string;
  };
};

export type QuestionnaireData = QuestionnaireShortData & {
  form: QuestionnaireForm;
};

export type FormSnippetShortData = {
  firm: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  id: string;
  name: string;
};

export type FormSnippetData = FormSnippetShortData & {
  data: {
    items: QuestionnaireItem[];
  };
};

export enum TrustAccountStatus {
  Unknown = 'Unknown',
  Created = 'Created',
  Provisioning = 'Provisioning',
  Inactive = 'Inactive',
  PendingVerification = 'PendingVerification',
  Active = 'Active',
  ProvisioningFailed = 'ProvisioningFailed',
  Blocked = 'Blocked',
}

export interface TrustAccountShort {
  type: 'Savings' | 'Checking';
  status: TrustAccountStatus;
  createdAt: string;
  updatedAt: string;
  id: number;
  name: string;
  accountNumber: string;
  routingNumber: string;
  caseTypes: Array<{
    id: number;
    name: string;
  }>;
  isEwsVerified: boolean;
  canBeDeleted: boolean;
  canUpdatePassportInfo: boolean;
}

export enum ProvisioningStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Error = 'Error',
  CriticalError = 'CriticalError',
  Completed = 'Completed',
}

export enum ExternalAccountStatus {
  Inactive = 'INACTIVE',
  PendingVerification = 'PENDING_VERIFICATION',
  Active = 'ACTIVE',
  Blocked = 'BLOCKED',
}

export enum FBOAccountStatus {
  Inactive = 'INACTIVE',
  PendingVerification = 'PENDING_VERIFICATION',
  Active = 'ACTIVE',
  Blocked = 'BLOCKED',
}

export enum EwsValidationStatus {
  Pending = 'PENDING',
  NotFound = 'NOT_FOUND',
  Closed = 'CLOSED',
  HighRisk = 'HIGH_RISK',
  InvalidDebitAccount = 'INVALID_DEBIT_ACCOUNT',
  Open = 'OPEN',
  Validated = 'VALIDATED',
}

export enum OnboardingDisplayStatus {
  completed = 'COMPLETED',
  inProgress = 'IN_PROGRESS',
  blocked = 'BLOCKED',
  inviteSent = 'INVITE_SENT',
  notStarted = 'NOT_STARTED',
  notApplicable = 'NOT_APPLICABLE',
}

export enum AutoSweepTransactionStatus {
  Pending = 'Pending',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Queued = 'Queued',
  Processing = 'Processing',
  RetryScheduled = 'RetryScheduled',
  RetryFailed = 'RetryFailed',
}

export interface TrustAccount extends Omit<TrustAccountShort, 'isEwsVerified'> {
  integration: {
    provisioning: {
      status: ProvisioningStatus;
      error?: {
        message?: string;
        retryAvailable: boolean;
      };
    };
    canStartProvisioning: boolean;
    externalAccount?: {
      status: ExternalAccountStatus;
      statusReason: string;
      ewsValidation?: {
        status: EwsValidationStatus;
        statusReason: string;
      };
      bank?: {
        name: string;
        routingNumber: string;
      };
    };
  };
}

export enum UnrestrictedAccountStatus {
  Unknown = 'Unknown',
  Created = 'Created',
  Provisioning = 'Provisioning',
  Inactive = 'Inactive',
  PendingVerification = 'PendingVerification',
  Active = 'Active',
  ProvisioningFailed = 'ProvisioningFailed',
  Blocked = 'Blocked',
}

export enum VendorStatus {
  new = 'NEW',
  confirmation_required_vendor = 'CONFIRMATION_REQUIRED_VENDOR',
  confirmation_required_admin = 'CONFIRMATION_REQUIRED_ADMIN',
  account_number_validation = 'ACCOUNT_NUMBER_VALIDATION',
  provisioning = 'PROVISIONING',
  failed = 'FAILED',
  active = 'ACTIVE',
  archived = 'ARCHIVED',
  exist = 'EXISTING',
  exist_in_vlaw = 'EXIST_IN_VLAW',
}

export enum DashboardApprovalStatus {
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  APPROVED_PROCESSING = 'APPROVED_PROCESSING',
  APPROVED_TO_BE_PAID = 'APPROVED_TO_BE_PAID',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  WAITING_FOR_SUBMISSION = 'WAITING_FOR_SUBMISSION',
  COMPLETED = 'COMPLETED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export interface UnrestrictedAccountShort {
  type: 'Savings' | 'Checking';
  status: UnrestrictedAccountStatus;
  createdAt: string;
  updatedAt: string;
  id: number;
  name: string;
  accountNumber: string;
  routingNumber: string;
  isEwsVerified: boolean;
  canBeDeleted: boolean;
  canUpdatePassportInfo: boolean;
  phoneNumber: string;
  email: string;
  confirmVendorProvisioning: boolean;
  vendorType: string;
  payeeType: string;
  paymentMethod: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface UnrestrictedAccount extends Omit<UnrestrictedAccountShort, 'isEwsVerified'> {
  integration: {
    provisioning: {
      status: ProvisioningStatus;
      error?: {
        message?: string;
        retryAvailable: boolean;
      };
    };
    canStartProvisioning: boolean;
    externalAccount?: {
      status: ExternalAccountStatus;
      statusReason: string;
      ewsValidation?: {
        status: EwsValidationStatus;
        statusReason: string;
      };
      bank?: {
        name: string;
        routingNumber: string;
      };
    };
  };
}

export enum VendorAccountStatus {
  Unknown = 'Unknown',
  Created = 'Created',
  Provisioning = 'Provisioning',
  Inactive = 'Inactive',
  PendingVerification = 'PendingVerification',
  Active = 'Active',
  ProvisioningFailed = 'ProvisioningFailed',
  Blocked = 'Blocked',
}

export interface VendorAccountShort {
  type: 'Savings' | 'Checking';
  vendorStatus: VendorStatus;
  createdAt: string;
  updatedAt: string;
  id: number;
  name: string;
  accountNumber: string;
  routingNumber: string;
  isEwsVerified: boolean;
  canBeDeleted: boolean;
  canUpdatePassportInfo: boolean;
  mailingPhoneNumber: string;
  email: string;
  isVendorConfirmationRequired: boolean;
  vendorType: string;
  defaultPaymentMethod: string;
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  tin: string | null;
  participationAddress1: string;
  participationAddress2: string;
  participationCity: string;
  participationState: string;
  participationZip: string;
  confirmationStatus?: string;
  contactName: string;
}

export interface VendorDocumentData {
  id: number;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByIsDeactivated?: boolean;
  uploadDate: string;
}

export interface VendorAccount extends Omit<VendorAccountShort, 'isEwsVerified'> {
  bank: VendorBankInfo;
  warningMessages?: string[];
}

// New type for the API response structure from getVendorAccount
export interface VendorMailingAddress {
  isKeptAsEntered: boolean;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  status?: ExternalAccountStatus;
  isAddressVerified?: boolean;
  provisioning?: {
    status: ProvisioningStatus;
    error?: {
      message: string;
      retryAvailable: boolean;
    };
  };
}

export interface VendorParticipationAddress {
  isKeptAsEntered: boolean;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  isAddressVerified?: boolean;
}

export interface VendorBankInfo {
  accountNumber: string;
  routingNumber: string;
  name: string;
  hasAccountNumber: boolean;
  defaultPaymentMethod: string;
  accountType: 'Savings' | 'Checking';
  status: string;
  canBeDeleted: boolean;
  canUpdatePassportInfo: boolean;
  integration: {
    provisioning: {
      status: ProvisioningStatus;
      error?: {
        message?: string;
        retryAvailable?: boolean;
      };
    };
    canStartProvisioning: boolean;
    externalAccount?: {
      status: ExternalAccountStatus;
      statusReason?: string;
      ewsValidation?: {
        status: EwsValidationStatus;
        statusReason?: string;
      };
    };
  };
}

export interface VendorChangedField {
  fieldName: string;
  originalValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  updatedValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  isAccountNumber?: boolean;
}

export interface VendorModificationHistory {
  modifiedAt: string; // ISO date string
  modifiedBy: string;
  changedFields: VendorChangedField[];
  reason: string;
}

export interface VendorAccountDetail {
  id: number;
  vendorId?: number;
  firmId?: number;
  name: string;
  tin: string | null;
  email: string;
  defaultPaymentMethod: string;
  vendorType: string;
  isVendorConfirmationRequired: boolean;
  vendorStatus?: string;
  createdAt: string;
  updatedAt: string;
  mailing: VendorMailingAddress;
  participationAddress: VendorParticipationAddress;
  bank: VendorBankInfo;
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
  history?: {
    modificationHistory: VendorModificationHistory;
  };
  contactName: string;
  passportIntegration?: {
    provisioningStatus: string;
    provisioningError?: string;
    retryAvailable?: boolean;
  };
  notes?: string;
  comments?: VendorCommentItem[];
  accounting?: {
    isSynced: boolean;
    status: string;
    externalVendorId?: string;
    lastSyncedAt?: string;
    link?: string;
  };
  warningMessages?: string[];
}

export interface vendorFBO {
  vendorParticipation: {
    id: number;
    name: string;
  };
  integration: {
    provisioning: {
      status: string;
      error: {
        reason: string;
        retryAvailable: boolean;
      };
    };
    ofac: {
      status: string;
      reason: string;
    };
    isOk: boolean;
  };
  canStartIntegration: boolean;
  accounts: {
    case: {
      accountNumber: string;
      routingNumber: string;
      balance: string;
      status: string;
      withdrawalAvailable: boolean;
    };
  };
}

export interface VendorCommentItem {
  id: number;
  comment: string;
  addedByName: string;
  addedByIsDeactivated?: boolean;
  addedByRole: string;
  addedAt: string;
  modificationType: string;
}

export interface VendorCommentsResponse {
  comments: VendorCommentItem[];
}

export interface searchVendorAccount {
  id: number;
  vendorId?: number;
  firmId?: number;
  name: string;
  tin: string;
  email: string;
  defaultPaymentMethod: string;
  vendorType: string;
  isVendorConfirmationRequired: boolean;
  vendorStatus?: string;
  createdAt: string;
  updatedAt: string;
  mailing: VendorMailingAddress;
  participationAddress: VendorParticipationAddress;
  bank: VendorBankInfo;
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
}

export type SecondaryFirmRole = 'CoCounsel' | 'ReferringCounsel';

export interface AttyFeesConfigData {
  case: {
    id: number;
    name: string;
  };
  config: {
    maximumAmount: number | null;
    isDraft: boolean;
    variableFeePercent: number;
    secondaryFirms: Array<{
      role: SecondaryFirmRole;
      id: number;
      name: string;
      config: {
        fixedFeeAmount: number | null;
        variableFeePercent: number | null;
        variableFeeMaximumAmount: number | null;
      };
    }>;
  };
}

export interface AttyFeesPaymentPreview {
  totalGrossSettlement: number;
  attorneysFees: {
    total: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
    };
    secondaryFirms: Array<{
      id: number;
      name: string;
      role: SecondaryFirmRole;
      fees: {
        fixed: number;
        variable: number;
        total: number;
      };
    }>;
  };
  deductions: {
    liens: number;
    advances: number;
    total: number;
    serviceProviders: number;
  };
  netSettlement: {
    clientId: number;
    amount: number;
  };
}

export type CounselEntityType = 'FIRM' | 'VENDOR';

export interface SettlementAttyFeesResponse {
  attorneysFees: {
    total: number;
    leadFirm: {
      attyFeeId: number;
      id: number;
      name: string;
      fee: number;
    };
    secondaryFirms: Array<{
      attyFeeId: number;
      id: number;
      counselEntityType: CounselEntityType;
      name: string;
      role: SecondaryFirmRole;
      fees: number;
      status: string;
      paymentMethod: string | null;
      vendorPaymentAccountId: number | null;
      memo?: string | null;
    }>;
  };
}

export type SettlementAttyFeesSecondaryPayload =
  | {
    // add
    counselEntityType: CounselEntityType;
    id: number;
    role: SecondaryFirmRole;
    feesTotalAmount: number;
    paymentMethod?: string;
    vendorPaymentAccountId?: number;
    memo?: string;
  }
  | {
    // edit
    attyFeeId: number;
    role: SecondaryFirmRole;
    feesTotalAmount: number;
    paymentMethod?: string;
    vendorPaymentAccountId?: number;
    memo?: string;
  }
  | {
    // delete
    attyFeeId: number;
    delete: true;
  };

export interface UpdateSettlementAttyFeesPayload {
  leadFirm: { attyFeeId: number; feesTotalAmount: number };
  secondaryCounsel: SettlementAttyFeesSecondaryPayload[];
}

export interface AttyFeesConfigResponse {
  paymentPreview: AttyFeesPaymentPreview;
  config: AttyFeesConfigData;
}

export interface PaymentPreviewData {
  totalGrossSettlement: number;
  attorneysFees: {
    total?: number;
    leadFirm?: {
      id: number;
      name: string;
      fee: number;
    };
    secondaryFirms: Array<{
      role: 'CoCounsel' | 'ReferringCounsel';
      id: number;
      name: string;
      fees: {
        fixed: number;
        variable: number;
        total: number;
      };
    }>;
  };
  deductions?: {
    liens: number;
    advances: number;
    total: number;
    serviceProviders: number;
  };
  netSettlement?: {
    plaintiffId: number;
    amount: number;
  };
}

export interface SaveAttyFeesConfigData {
  case: {
    variableFeePercent?: number | null;
    maximumAmount?: number | null;
  };
  secondaryFirms: Array<{
    fixedAmount?: number | null;
    variableFeePercent?: number | null;
    maximumAmount?: number | null;
    firmId: number;
  }>;
}

export interface ResultSet<Type> {
  resultSet: Type[];
  totalCount: number | null;
  blockedCount?: number;
}

export interface FirmTransactionsResultSet extends ResultSet<FirmTransaction> {
  accounts: {
    case: {
      balance: string;
      projectedBalance: string;
    };
    rev: {
      balance: string;
      projectedBalance: string;
    };
    exp: {
      balance: string;
      projectedBalance: string;
    };
  };
}

export interface PaymentHistoryResultSet<Type> {
  resultSet: Type[];
  totalCount: number | null;
  isAccountingIntegrated?: boolean;
}

export interface SettlementResultSet<Type> {
  resultSet: Type[];
  totalCount: number | null;
  enableMemoUpload: boolean;
}

export interface DashboardSettlementResultSet<Type> {
  resultSet: Type[];
  totalCount: number | null;
  processingStatusCount?: {
    awaitingApprovalCount: number;
    approvedProcessingCount: number;
    approvedToBePaidCount: number;
    changesRequestedCount: number;
    completedCount: number;
  };
}

export interface FirmTransaction {
  type: 'CREDIT' | 'DEBIT';
  scheduleClass: 'SEND' | 'COLLECT';
  method:
  | 'BOOK'
  | 'ACH'
  | 'WIRE'
  | 'CARD'
  | 'CHECK'
  | 'VIRTUAL_CARD'
  | 'INTERNATIONAL_WIRE'
  | 'CASH';
  amount: string;
  runningBalance: string;
  lastUpdatedOn: string;
  transactionId: number;
  accountId: number;
  memo: string;
  status: string | null;
  accountName: string;
  caseReferenceId: string | null;
  plaintiffName: string | null;
  destinationAccount:
    | {
        name: string;
        accountNumber: string;
        routingNumber: string;
        type: string;
      }
    | null;
}

export type StatusMap<T extends string | number | symbol> = Record<
  T,
  { color: string; label: string }
>;

export interface VendorFileUploadHistory {
  id: number;
  originalFileName: string;
  status: 'UPLOADED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  totalRows: number;
  successCount: number;
  failureCount: number;
  startedAt: string;
  completedAt?: string;
  errorFileDownloadUrl?: string | null;
}
export type memoCaseDetails = {
  id?: number;
  name?: string;
  referenceId?: string;
  typeName?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  fullName?: string;
};

export interface SettlementMemoResultSet {
  caseId?: number;
  settledAmount?: number;
  pipAmount?: number;
  settlementType?: 'STANDARD' | 'STANDARD_PIP';
  attyFee?: number;
  attorneysFees?: {
    total: number;
    feePrecentage: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
      accounting?: {
        isSynced: boolean;
        status: string;
        externalVendorId: string | null;
        lastSyncedAt: string | null;
        link: string | null;
      };
    };
    secondaryFirms: Array<{
      id: number;
      name: string;
      fee: number;
      role: 'CoCounsel' | 'ReferringCounsel';
      accounting?: {
        isSynced: boolean;
        status: string;
        externalVendorId: string;
        lastSyncedAt: string;
        link: string;
      };
      disposition: string;
      paymentMethod: string;
    }>;
  };
  deductions?: Array<{
    vendorName: string;
    amount: number;
    deductionType: 'Advance' | 'Lien';
  }>;
  hasErrors?: boolean;
  canConfirmPdf: boolean;
  hasWarnings?: boolean;
  warningMessages?: string[];
  errorMessages?: string[];
  fileHash?: string;
  caseInfo?: {
    id: number;
    name: string;
    referenceId: string;
    type: {
      name: string;
    };
    defendant?: [
      {
        id: number;
        name: string;
      },
    ];
  };
}

/**
 * Response from save/update/get draft operations
 * This matches the backend FoPdfSettlementResponse structure with added draft metadata
 * Note: This is different from SettlementMemoResultSet which is used for PDF upload validation
 */
export interface MemoDraftResponse {
  draftId: number;
  createdAt: string; // ISO 8601 format: "2026-07-09T10:30:00.000Z"
  updatedAt: string; // ISO 8601 format: "2026-07-09T10:30:00.000Z"

  // Settlement data from FoPdfSettlementResponse
  id?: number;
  caseId?: number;
  settledAmount?: number;
  pipAmount?: number;
  settlementType?: 'STANDARD' | 'STANDARD_PIP';
  status?: string;

  case?: {
    id: number;
    name: string;
    referenceId: string;
    type?: {
      name: string;
    };
  };

  client?: {
    id: number;
    firstName: string;
    middleName: string | null;
    lastName: string;
  };

  defendant?: {
    id: number;
    name: string;
  };

  attorneysFees?: {
    total: number;
    feePrecentage: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
      role?: string;
      disposition?: string;
      paymentMethod?: string;
      accounting?: {
        isSynced: boolean;
        status: string;
        externalVendorId: string | null;
        lastSyncedAt: string | null;
        link: string | null;
      };
    } | null;
    secondaryFirms: Array<{
      id: number;
      name: string;
      fee: number;
      role: 'CoCounsel' | 'ReferringCounsel';
      disposition?: string;
      paymentMethod?: string;
      accounting?: {
        isSynced: boolean;
        status: string;
        externalVendorId: string | null;
        lastSyncedAt: string | null;
        link: string | null;
      };
    }>;
  };

  deductions?: Array<{
    id: number;
    type: 'Advance' | 'Lien';
    amount: number;
    vendorParticipationId: number | null;
    vendorName?: string;
    vendorSuggestions?: Array<{
      vendorParticipationId: number;
      vendorName: string;
      matchScore?: number;
    }>;
    paymentMethod?: string;
    disposition?: string;
    memo?: string;
    originalText?: string;
    description?: string;
    matchingMode?: string;
    accounting?: {
      isSynced: boolean;
      status: string;
      externalVendorId: string | null;
      lastSyncedAt: string | null;
      link: string | null;
    };
    provisioning?: {
      ach: { isProvisioned: boolean };
      check: { isProvisioned: boolean };
    };
  }>;

  netSettlement?: {
    id: number;
    clientId: number;
    amount: number;
    name: string;
    accounting?: {
      isSynced: boolean;
      status: string;
      externalVendorId: string | null;
      lastSyncedAt: string | null;
      link: string | null;
    };
  };

  filename?: string;
  docpath?: string;
  fileHash?: string;
  warningMessages?: string[];
  isAccountingIntegrated?: boolean;
}

/**
 * Summary view of draft (used in list responses)
 */
export interface MemoDraftSummary {
  draftId: number;
  caseId: number;
  caseReferenceId: string; // e.g., "CASE-2026-001"
  caseName: string; // e.g., "Smith v. ABC Corporation"
  clientName: string; // Full name: "John Michael Smith"
  defendantName: string; // e.g., "ABC Corporation"
  settledAmount: number; // e.g., 250000.00
  filename?: string; // e.g., "settlement_memo_smith.pdf"
  createdAt: string; // ISO 8601 format
  updatedAt: string; // ISO 8601 format
}

/**
 * Paginated list response structure
 */
export interface PaginatedMemoDrafts {
  data: MemoDraftSummary[]; // Array of draft summaries
  total: number; // Total count of drafts
  page: number; // Current page number (1-indexed)
  limit: number; // Items per page (default: 10)
  totalPages: number; // Total number of pages
}

export interface ApprovalTimelineEvent {
  createdAt: string;
  id: number;
  eventType: string;
  eventSubType: string;
  message: string;
  comment: string;
  roleCode: string;
  actedBy: string;
  actedByName: string;
  actedByIsDeactivated?: boolean;
  actedByRole: string;
}

export interface ApprovalFlowDetails {
  canInitiatePayment: boolean;
  payGateStatus: string;
  blockingReason: string;
  blockingReasonCode: string;
  activeCycleStatus: string;
  activeCycleNo: number;
  canOverride: boolean;
  approvalTimeline: ApprovalTimelineEvent[];
  approvalRequired: boolean;
  approvals: Array<{
    time: string;
    approvalComment: string;
    approver: string;
    approverIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;
  requestChanges: Array<{
    time: string;
    changeComment: string;
    requester: string;
    requesterIsDeactivated?: boolean;
    roleCode: string;
    isRequired: boolean;
  }>;
  pendingApprovals: Array<{
    roleCode: string;
    isRequired: boolean;
  }>;
}

export type PaymentHistoryData = {
  caseId: number;
  updatedAt: string;
  caseReferenceId: string;
  plaintiffName: string;
  type: string;
  method: string;
  amount: number;
  status: string;
  billLink: string | null;
  billError: string | null;
  billSyncStatus?: AccountingSyncStatus | null;
  billPaymentLink: string | null;
  billPaymentError: string | null;
  billPaymentSyncStatus?: AccountingSyncStatus | null;
  vendorLink: string | null;
  checkDetails?: {
    payee: string | null;
    nickname: string | null;
    payeeName: string | null;
    recipientName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    countryState: string | null;
    zip: string | null;
    phoneNumber: string | null;
  } | null;
  achDetails?: {
    payee: string | null;
    nickname: string | null;
    accountHolderName: string | null;
    accountType: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
  } | null;
};

export type LedgerSettlementOption = {
  settlementId: number;
  label: string;
  type: string;
  creditAmount: number;
  date: string;
};

export type LedgerTile = {
  balance: number;
  credits: number;
  debits: number;
};

export type LedgerTiles = {
  posted: LedgerTile;
  inTransit: LedgerTile;
  projected: LedgerTile & { pendingLegs: number };
};

export type LedgerEntry = {
  date: string;
  description: string;
  category: string;
  credit: number;
  debit: number;
  balance: number;
  status: string;
  settlementId: number | null;
  deductionId?: number | null;
  settlementSequenceId: number | null;
  sqNo: number | null;
  sourceType: string;
  sourceId: number | null;
};

export type CaseLedgerResponse = {
  settlementOptions: LedgerSettlementOption[];
  tiles: LedgerTiles;
  entries: LedgerEntry[];
};

export type AccountingVendorField = {
  name: string;
  label: string;
  type: string;
  required: boolean;
  minLength?: number;
  maxLength?: number;
  trim?: boolean;
  noWhitespaceOnly?: boolean;
  format?: string;
};

export type AccountingVendor = {
  accountingSystem: AccountingSystem;
  fields: AccountingVendorField[];
  providerFields: AccountingVendorField[];
};

export type QuickBookVendorFields = {
  name: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  providerData: {
    companyName: string;
  };
  nativeEntityId: number;
  nativeEntityType: 'CLIENT' | 'VENDOR' | string;
  accountingEntityType: 'VENDOR' | string;
};

export enum AccountingSystem {
  NETSUITE = 'NETSUITE',
  QUICKBOOKS_ONLINE = 'QUICKBOOKS_ONLINE'
}
